import { twMerge } from 'tailwind-merge';

import { useImageLoadingStatus } from '@/components/react-aria/hooks/use-image-loading-status';
import { randomSeed } from '@/renderer/utils/randomSeed';

const COLORS = [
  'bg-orange-500',
  'bg-yellow-500',
  'bg-lime-500',
  'bg-cyan-500',
  'bg-sky-500',
  'bg-blue-500',
  'bg-indigo-500',
  'bg-violet-500',
  'bg-purple-500',
  'bg-fuchsia-500',
  'bg-pink-500',
  'bg-rose-500',
] as const;

export default function Avatar({
  user,
  isAdmin,
}: {
  user?: { firstName: string; lastName: string; id: API.Id; avatarUrl?: string | null };
  isAdmin?: boolean;
} = {}) {
  const random = randomSeed(`${user?.id}`);
  const bg = COLORS[Math.floor(random * COLORS.length - 0.0001)];

  // Render the uploaded photo only once it has actually loaded (#1999); until
  // then — and if it errors or is absent — we keep the initials tile, so a
  // broken/slow URL never leaves a blank square.
  const avatarUrl = user?.avatarUrl || undefined;
  const status = useImageLoadingStatus(avatarUrl);
  const showPhoto = !!avatarUrl && status === 'loaded';

  return (
    <div
      className={twMerge(
        'relative mt-[3px] inline-flex h-8 w-8 select-none items-center justify-center overflow-hidden rounded-sm',
        bg,
        isAdmin && 'bg-emerald-700 dark:bg-emerald-500',
      )}
    >
      {showPhoto ? (
        <img
          src={avatarUrl}
          alt=""
          aria-hidden
          className="absolute inset-0 h-full w-full object-cover"
        />
      ) : (
        <span className="select-none uppercase text-white">
          {user?.firstName?.charAt(0)}
          {user?.lastName?.charAt(0)}
        </span>
      )}
    </div>
  );
}
